import { useState } from 'react';
import { ChevronLeft, Star, Battery, Zap, Target, Settings, Shield, CheckCircle2 } from 'lucide-react';
import { useMachines, useReviews, useFAQs, useRentalPackages } from '../../hooks/useAPI';
import ptImage from '../../imports/pt.jpg';

interface ProductDetailScreenProps {
  onNavigate: (screen: any) => void;
  onBook: () => void;
}

export default function ProductDetailScreen({ onNavigate, onBook }: ProductDetailScreenProps) {
  const { data: machines, isLoading: machinesLoading } = useMachines();
  const { data: reviews, isLoading: reviewsLoading } = useReviews();
  const { data: faqs, isLoading: faqsLoading } = useFAQs();
  const { data: packages, isLoading: packagesLoading } = useRentalPackages();

  const [currentImage, setCurrentImage] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const machine = machines && machines.length > 0 ? machines[0] : null;
  const displayReviews = reviews || [];
  const displayFaqs = faqs || [];
  const rentalPackages = packages || [];

  if (machinesLoading) {
    return (
      <div className="w-full min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">🎾</div>
          <p className="text-muted-foreground">Loading machine details...</p>
        </div>
      </div>
    );
  }

  if (!machine) {
    return (
      <div className="w-full min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Machine not found</p>
          <button
            onClick={() => onNavigate('home')}
            className="h-12 px-6 rounded-xl bg-[#1A6B4F] text-white"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-background pb-32">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => onNavigate('home')}
          className="w-10 h-10 rounded-full hover:bg-secondary transition-colors flex items-center justify-center"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="text-lg">Product Details</h1>
      </div>

      <div className="relative h-80 bg-white flex items-center justify-center overflow-hidden">
        <img
          src={ptImage}
          alt="PT-9001 Tennis Ball Machine"
          className="w-full h-full object-contain"
        />
      </div>

      <div className="px-6 py-6 space-y-6">
        <div>
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <div className="inline-block px-3 py-1 bg-[#D4F04A]/20 rounded-full text-xs text-[#1A6B4F] mb-2">
                Professional Machine
              </div>
              <h2 className="text-3xl mb-1">{machine.name}</h2>
              <p className="text-muted-foreground">{machine.type}</p>
            </div>
            <div className="flex items-center gap-1 bg-secondary px-3 py-2 rounded-xl">
              <Star className="w-4 h-4 fill-[#D4F04A] text-[#D4F04A]" />
              <span className="text-sm">4.9</span>
            </div>
          </div>
          <p className="text-sm text-foreground/80 leading-relaxed mt-4">
            {machine.description}
          </p>
        </div>

        <div>
          <h3 className="text-lg mb-3">Key Features</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="w-10 h-10 rounded-xl bg-[#D4F04A]/20 flex items-center justify-center mb-3">
                <Settings className="w-5 h-5 text-[#1A6B4F]" />
              </div>
              <div className="text-sm mb-1">App Control</div>
              <div className="text-xs text-muted-foreground">Smartphone control</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="w-10 h-10 rounded-xl bg-[#D4F04A]/20 flex items-center justify-center mb-3">
                <Target className="w-5 h-5 text-[#1A6B4F]" />
              </div>
              <div className="text-sm mb-1">35 Landing Points</div>
              <div className="text-xs text-muted-foreground">Programmable drills</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="w-10 h-10 rounded-xl bg-[#D4F04A]/20 flex items-center justify-center mb-3">
                <Zap className="w-5 h-5 text-[#1A6B4F]" />
              </div>
              <div className="text-sm mb-1">20-140 km/h</div>
              <div className="text-xs text-muted-foreground">Variable speed</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="w-10 h-10 rounded-xl bg-[#D4F04A]/20 flex items-center justify-center mb-3">
                <Battery className="w-5 h-5 text-[#1A6B4F]" />
              </div>
              <div className="text-sm mb-1">7-10 Hours</div>
              <div className="text-xs text-muted-foreground">Battery life</div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg mb-3">All Features</h3>
          <div className="grid grid-cols-1 gap-2">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#1A6B4F] flex-shrink-0" />
              <span className="text-sm">Smartphone app control via WiFi/Bluetooth</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#1A6B4F] flex-shrink-0" />
              <span className="text-sm">35 programmable landing points for realistic drills</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#1A6B4F] flex-shrink-0" />
              <span className="text-sm">Variable speed from 20 to 140 km/h</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#1A6B4F] flex-shrink-0" />
              <span className="text-sm">7-10 hours battery life on single charge</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#1A6B4F] flex-shrink-0" />
              <span className="text-sm">250 ball capacity hopper</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#1A6B4F] flex-shrink-0" />
              <span className="text-sm">Adjustable elevation and oscillation</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#1A6B4F] flex-shrink-0" />
              <span className="text-sm">Built-in training programs and custom drills</span>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg mb-3">Technical Specifications</h3>
          <div className="bg-card border border-border rounded-2xl divide-y divide-border">
            <div className="p-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Ball Capacity</span>
              <span className="text-sm">250 balls</span>
            </div>
            <div className="p-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Battery Life</span>
              <span className="text-sm">7-10 hours</span>
            </div>
            <div className="p-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Serve Frequency</span>
              <span className="text-sm">2-10 seconds</span>
            </div>
            <div className="p-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Speed Range</span>
              <span className="text-sm">20-140 km/h</span>
            </div>
            <div className="p-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Max Distance</span>
              <span className="text-sm">23 meters</span>
            </div>
            <div className="p-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Elevation Angle</span>
              <span className="text-sm">-15° to +50°</span>
            </div>
            <div className="p-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Weight</span>
              <span className="text-sm">47 kg</span>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg mb-3">Rental Pricing</h3>
          {packagesLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-secondary rounded-2xl p-4 h-16 animate-pulse" />
              ))}
            </div>
          ) : rentalPackages.length > 0 ? (
            <div className="space-y-2">
              {rentalPackages.slice(0, 5).map((pkg: any) => (
                <div
                  key={pkg.id}
                  className={`bg-card border ${
                    pkg.is_popular ? 'border-[#D4F04A] shadow-lg' : 'border-border'
                  } rounded-2xl p-4`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{pkg.name}</span>
                        {pkg.is_popular && (
                          <span className="px-2 py-0.5 bg-[#D4F04A] text-[#1a1a1a] text-xs rounded-full">
                            Most Popular
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">{pkg.duration_hours} hours</span>
                    </div>
                    <span className="text-lg text-[#1A6B4F]">AED {pkg.price}</span>
                  </div>
                  <button
                    onClick={onBook}
                    className="w-full h-9 rounded-lg bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm transition-colors"
                  >
                    Book Now
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-4">No packages available</div>
          )}
          <div className="mt-4 p-4 bg-secondary rounded-xl">
            <div className="flex items-start gap-2 text-sm">
              <Shield className="w-4 h-4 text-[#1A6B4F] mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-foreground">Security Deposit: AED {rentalPackages[0]?.deposit_required || 500}</div>
                <div className="text-muted-foreground text-xs mt-1">
                  Fully refundable upon return in good condition
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg">Customer Reviews</h3>
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-[#D4F04A] text-[#D4F04A]" />
              <span className="text-sm">4.9 ({displayReviews.length} reviews)</span>
            </div>
          </div>
          {reviewsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-secondary rounded-2xl p-4 h-24 animate-pulse" />
              ))}
            </div>
          ) : displayReviews.length > 0 ? (
            <div className="space-y-3">
              {displayReviews.slice(0, 5).map((review: any) => (
                <div key={review.id} className="bg-card border border-border rounded-2xl p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-sm">{review.profiles?.full_name || 'Anonymous'}</div>
                      <div className="text-xs text-muted-foreground">Verified Customer</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-[#D4F04A] text-[#D4F04A]" />
                      <span className="text-sm">{review.rating}</span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {review.comment}
                  </p>
                  <div className="text-xs text-muted-foreground mt-2">
                    {new Date(review.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-4">No reviews yet</div>
          )}
        </div>

        <div>
          <h3 className="text-lg mb-3">Frequently Asked Questions</h3>
          {faqsLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="bg-secondary rounded-xl p-4 h-12 animate-pulse" />
              ))}
            </div>
          ) : displayFaqs.length > 0 ? (
            <div className="space-y-2">
              {displayFaqs.slice(0, 5).map((faq: any, index: number) => (
                <div key={faq.id} className="bg-card border border-border rounded-xl overflow-hidden">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full p-4 text-left flex items-center justify-between hover:bg-secondary transition-colors"
                  >
                    <span className="text-sm pr-4">{faq.question}</span>
                    <span className={`text-xl transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`}>
                      ⌄
                    </span>
                  </button>
                  {expandedFaq === index && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-4">No FAQs available</div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border p-4">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="text-xs text-muted-foreground">Starting from</div>
            <div className="text-xl text-[#1A6B4F]">
              AED {rentalPackages.length > 0 ? rentalPackages[0].price : '180'}
            </div>
          </div>
          <button
            onClick={onBook}
            className="flex-1 h-14 rounded-2xl bg-[#1A6B4F] hover:bg-[#14563F] text-white transition-colors"
          >
            Book Now
          </button>
        </div>
      </div>
    </div>
  );
}
